import * as React from 'react';
import { IProductLookupProps } from './ProductLookup.properties';
import { getProductLookupColumns, getProductSearchColumns, SEARCH_SCREEN_DIALOG_STYLE } from './constants';
import { useFetchWarehouseProductLookup, useFetchWarehouseProductSummary } from 'api/product/productSearchUpdated';
import { IBasicLookupFieldHandle } from '@markinson/uicomponents-v2/BasicLookupField/BasicLookup.properties';
import BasicLookupField from '@markinson/uicomponents-v2/BasicLookupField';
import { CustomTypesApi } from 'api/customType';
import { WithStyles, withStyles } from '@material-ui/core';
import styles from '../ProductLookup/ProductLookup.styles';

const ProductLookupBatchSize = 100;
const ProductLookup = (props: Readonly<IProductLookupProps & WithStyles<typeof styles>>, ref: React.Ref<IBasicLookupFieldHandle>) => {
    const { Warehouse, CustomerId, onSelectedItemChange, onChange, DefaultWarehouse, fieldRef, classes, LookupLabel, onValueChange, placeholder, ...restProps } = props;
    const [currentSearchType, setCurrentSearchType] = React.useState<'Lookup' | 'Search'>();
    const useProductLookupMutation = useFetchWarehouseProductLookup();
    const useFetchSummaryMutation = useFetchWarehouseProductSummary();
    const [okButtonDisabled, setOkButtonDisabled] = React.useState(false);

    React.useEffect(
        () => {
            setCurrentSearchType('Lookup');
        },
        []
    );

    const retrieveProducts = React.useCallback(
        async (searchText, searchType, BatchPage?, BatchSize?, Sort?: string) =>
            useProductLookupMutation.mutateAsync
                ({
                    Warehouse: Warehouse || DefaultWarehouse,
                    CustomerId: CustomerId && Number(CustomerId) ? Number(CustomerId) : 0,
                    SearchText: searchText || '',
                    Type: searchType || 'Search',
                    AlternateProductCheck: true,
                    BatchPage,
                    BatchSize,
                    Sort
                }),
        [CustomerId, Warehouse, DefaultWarehouse]
    );

    const retrieveProductSummary = React.useCallback(
        async (item: any) => {
            if (item?.Details?.ProductId && (Warehouse || DefaultWarehouse)) {
                const response = await useFetchSummaryMutation.mutateAsync({
                    ProductId: item?.Details?.ProductId,
                    Warehouse: Warehouse || DefaultWarehouse
                });
                if (response && response?.Status) {

                    return response?.ProductLookupSummary;
                }

            }

            return null;
        },
        [Warehouse, DefaultWarehouse]
    );

    const onSearch = React.useCallback(
        async (searchText: string | undefined, batchSize: number, batchPage: number, exactMatch?: boolean): Promise<any> => {
            if (exactMatch) {
                setCurrentSearchType('Lookup');
            } else {
                setCurrentSearchType('Search');
            }
            const data = await retrieveProducts(searchText, exactMatch ? 'Lookup' : 'Search', batchPage, batchSize)
                .then((response) => response?.Data);

            return data;
        },
        [CustomerId, Warehouse]
    );

    const onFindOption = React.useCallback(
        async (searchText: string | undefined): Promise<any> => {
            const data = await CustomTypesApi.lookupGet('Product')(searchText);

            return data;
        },
        [CustomerId, Warehouse]
    );

    const onModuleChange = React.useCallback(
        (moduleId) => {
            setCurrentSearchType(moduleId);
        },
        []
    );

    const handleOnSelectedItemChanged = (selectedItem, searchText) => {
        const productId = selectedItem?.Details?.ProductId ? Number(selectedItem?.Details?.ProductId) : selectedItem?.Code;

        if (onChange) {
            const { disableOptionsLookup, suppressValidation } = props || {};
            const selectedValue = !!(disableOptionsLookup && suppressValidation) ? searchText : productId;
            onChange(selectedValue || '');
        }

        if (onSelectedItemChange) {
            onSelectedItemChange(selectedItem, searchText);
        }
    };

    const handleOnSelectionChange = (item: any) => {
        if (item) {
            if (item?.Details?.ProductId === 0 && item?.Details?.ProductGroup === '') {
                setOkButtonDisabled(true);
            } else {
                setOkButtonDisabled(false);
            }
        }
    };

    const onRowPrepared = ({ rowType, data, rowElement }): void => {
        if (rowType === 'data' && !Boolean(data?.Details?.ProductId)) rowElement?.classList?.add(classes.inactiveRow);
    };

    return (

        <BasicLookupField
            {...restProps}
            innerRef={fieldRef ? fieldRef : ref}
            valueField={'Code'}
            displayField={'Display'}
            label={LookupLabel ?? 'Product'}
            descriptionField={'Label'}
            placeholder={placeholder ?? 'Product'}
            onFindOption={onFindOption}
            batchSize={ProductLookupBatchSize}
            lookupOverrides={
                {
                    columns: currentSearchType === 'Lookup' ? getProductLookupColumns(Warehouse || DefaultWarehouse) : getProductSearchColumns(Warehouse || DefaultWarehouse),
                    dialogStyle: SEARCH_SCREEN_DIALOG_STYLE,
                    hideColumnHeaders: false,
                    disableGridColumnLines: false
                }
            }
            onSearch={onSearch}
            column1Field={'FormattedValue'}
            column2Field={'Description'}
            isTabbedLookup={true}
            searchScreenTitle={currentSearchType === 'Lookup' ? 'Product Lookup' : 'Product Search'}
            onModuleChange={onModuleChange}
            onFetchSummary={retrieveProductSummary}
            shouldEnterBehaveAsTab={true}
            onSelectedItemChange={handleOnSelectedItemChanged}
            onChange={onValueChange}
            onSelectionChange={handleOnSelectionChange}
            isOkButtonDisabled={okButtonDisabled}
            dataGridOnRowPrepared={onRowPrepared}
        />);
};

export default withStyles(styles, { index: 1 })(React.forwardRef(ProductLookup));
